/**
 * Real per-platform media limits, mirrored from what our publishers enforce.
 * Single source for every "how much of this post does the platform show" line
 * in the UI. Keep in sync with the backend when a publisher changes:
 *
 * - Instagram: backend/src/services/instagram-publisher.service.ts (carousel 2-10 items)
 * - Facebook:  backend/src/services/facebook-publisher.service.ts (max 10 images)
 * - LinkedIn:  backend/src/services/linkedin-publisher.service.ts (multiImage 2-20)
 * - Threads:   backend/src/types/threads.types.ts (maxCarouselItems 20)
 * - Bluesky:   backend/src/types/bluesky.types.ts (maxImages 4)
 * - TikTok:    backend/src/types/tiktok.types.ts (TIKTOK_PHOTO_REQUIREMENTS.maxImages 35)
 * - Google Business: backend/src/types/gmb.types.ts (MAX_IMAGES 1) and
 *   gmb-publisher.service.ts (video-only post publishes its thumbnail as the
 *   photo, text-only when it has none)
 * - Pinterest: backend/src/services/pinterest-publisher.service.ts (one image or one video)
 * - YouTube Shorts: backend/src/services/youtube-publisher.service.ts isShortVideo()
 *   (duration <= 60s AND height >= width)
 */

/** Feed-style platforms with a multi-image limit, in no particular order. */
export const FEED_IMAGE_LIMITS = {
  instagram: 10,
  facebook: 10,
  linkedin: 20,
  threads: 20,
  bluesky: 4,
} as const;

export const PLATFORM_MEDIA_LIMITS = {
  /** Instagram and Facebook carousels: the cap of the "main" feed coverage */
  feedCarouselImages: 10,
  /** The strictest feed platform (Bluesky) */
  strictestFeed: { name: 'Bluesky', images: FEED_IMAGE_LIMITS.bluesky },
  tiktokPhotoImages: 35,
  googleBusiness: { name: 'Google', images: 1 },
  pinterest: { name: 'Pinterest', images: 1 },
  youtubeShorts: { maxSeconds: 60 },
} as const;

export interface VideoShape {
  duration?: number | null;
  width?: number | null;
  height?: number | null;
}

/**
 * Whether YouTube publishes the video as a Short (mirrors the backend rule).
 * Returns null when duration or dimensions are unknown: callers say nothing then.
 */
export const isYoutubeShort = (video: VideoShape): boolean | null => {
  const { duration, width, height } = video;
  if (!duration || !width || !height) return null;
  return duration <= PLATFORM_MEDIA_LIMITS.youtubeShorts.maxSeconds && height >= width;
};
